import { useCallback, useEffect, useRef, useState } from "react";
import { Brain, Clock3, Flame, Lightbulb, Loader2, RotateCcw, Sparkles, Trophy } from "lucide-react";
import { API } from "@/lib/gp";
import { Button } from "@/components/ui/button";

const post = async (path, body) => {
  const res = await fetch(`${API}${path}`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Yorick shuffled the deck. Try again.");
  return data;
};

export default function YorickGauntlet({ embedded = false }) {
  const [game, setGame] = useState(null);
  const [question, setQuestion] = useState(null);
  const [seconds, setSeconds] = useState(60);
  const [score, setScore] = useState(0);
  const [hint, setHint] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const submitting = useRef(false);

  const start = useCallback(async () => {
    setBusy(true); setError(""); setFeedback(null); setResults(null); setHint("");
    try {
      const data = await post("/gauntlet/start");
      if (data.completed) setResults({ score: data.score, correct: data.correct, total: data.total, streak: data.streak, alreadyPlayed: true });
      else { setGame({ sessionId: data.session_id }); setQuestion(data.question); setScore(data.score); setSeconds(data.time_limit); }
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { start(); }, [start]);

  const answer = useCallback(async (choiceIndex) => {
    if (!question || !game || submitting.current || feedback) return;
    submitting.current = true; setBusy(true);
    try {
      const data = await post("/gauntlet/answer", { session_id: game.sessionId, question_id: question.id, choice_index: choiceIndex });
      setScore(data.score); setFeedback({ ...data, selected: choiceIndex });
      if (data.completed) setResults({ ...data.results, pending: true });
    } catch (e) { setError(e.message); }
    finally { submitting.current = false; setBusy(false); }
  }, [feedback, game, question]);

  useEffect(() => {
    if (!question || feedback || results?.alreadyPlayed) return undefined;
    const timer = window.setInterval(() => setSeconds((value) => {
      if (value <= 1) { window.clearInterval(timer); answer(-1); return 0; }
      return value - 1;
    }), 1000);
    return () => window.clearInterval(timer);
  }, [answer, feedback, question, results]);

  const next = () => {
    if (results?.pending) { setResults(({ pending, ...rest }) => rest); setFeedback(null); return; }
    setQuestion(feedback.next_question); setSeconds(feedback.time_limit); setFeedback(null); setHint(""); setError("");
  };

  const revealHint = async () => {
    if (hint || busy) return;
    setBusy(true);
    try { const data = await post("/gauntlet/hint", { session_id: game.sessionId, question_id: question.id }); setHint(data.hint); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className={embedded ? "border-t border-white/10" : "mx-auto max-w-[1440px] px-5 md:px-8 py-8"} aria-labelledby="gauntlet-title" data-testid="yorick-gauntlet">
      <div className={embedded ? "" : "glass rounded-3xl overflow-hidden ring-1 ring-gp-magic/40 shadow-[0_0_50px_rgba(139,92,246,0.12)]"}>
        <div className={embedded ? "" : "grid lg:grid-cols-[320px_1fr]"}>
          {!embedded && <div className="relative min-h-[390px] bg-gradient-to-br from-gp-magic/25 via-gp-blue/15 to-black/20 p-7 overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-52 lg:h-56 pointer-events-none">
              <img
                src="/yorick-challenge.jpg"
                alt="Yorick, GamePlat's trivia host"
                className="h-full w-full object-contain object-top opacity-95 drop-shadow-[0_0_24px_rgba(37,99,235,0.45)]"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#101928]" />
            </div>
            <div className="relative z-10 pt-40 lg:pt-44">
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gp-gold"><Sparkles size={14} /> Daily challenge</span>
              <h2 id="gauntlet-title" className="font-unbounded font-black text-2xl mt-3 leading-tight">Challenge Yorick to earn points</h2>
              <p className="text-sm text-white/60 mt-3">A fresh four-question game-learning challenge every day. Return tomorrow to keep your streak alive.</p>
            </div>
          </div>}

          <div className={embedded ? "p-5 md:p-6 bg-black/20" : "p-5 sm:p-7 md:p-9 min-h-[390px] flex flex-col justify-center"}>
            {embedded && <div className="mb-5 flex items-center gap-4"><img src="/yorick-challenge.jpg" alt="Yorick, GamePlat's challenge host" className="h-24 w-24 shrink-0 rounded-2xl object-cover object-top ring-1 ring-gp-blue/35" /><div><span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gp-gold"><Sparkles size={14} /> Daily challenge</span><h2 id="gauntlet-title" className="font-unbounded font-black text-xl mt-2">Test your game knowledge with Yorick</h2><p className="text-sm text-white/55 mt-2">Four questions. Every correct answer earns 5 points.</p></div></div>}
            {busy && !question && <div className="grid place-items-center gap-3 text-white/60"><Loader2 className="animate-spin text-gp-blue" /><span>Yorick is dealing today's cards…</span></div>}
            {error && <div role="alert" className="rounded-xl border border-gp-red/30 bg-gp-red/10 p-4 text-sm text-white"><p>{error}</p><Button onClick={start} variant="outline" className="mt-3 rounded-full border-white/20"><RotateCcw size={15} className="mr-2" /> Try again</Button></div>}
            {results && !results.pending && !error && (
              <div className="text-center" aria-live="polite">
                <Trophy size={42} className="mx-auto text-gp-gold" />
                <p className="text-xs uppercase tracking-[0.2em] text-gp-gold mt-3">{results.alreadyPlayed ? "Today's run complete" : "Gauntlet cleared"}</p>
                <h3 className="font-unbounded font-black text-3xl mt-2">{results.score} points</h3>
                <p className="text-white/65 mt-2">{results.correct} of {results.total} correct · {results.streak} day streak</p>
                <p className="text-sm text-white/45 mt-5">Come back tomorrow for a new deck and another chance to build your streak.</p>
              </div>
            )}
            {question && !error && !(results && !results.pending) && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex flex-wrap gap-2"><span className="rounded-full bg-gp-blue/15 px-3 py-1 text-gp-blue">{question.game}</span><span className="rounded-full bg-white/5 px-3 py-1 text-white/60">{question.category} · {question.difficulty}</span></div>
                  <div className="flex gap-4"><span className="flex items-center gap-1 text-gp-gold"><Trophy size={15} /> {score}</span><span className={`flex items-center gap-1 ${seconds <= 10 ? "text-gp-red" : "text-white/70"}`}><Clock3 size={15} /> {seconds}s</span></div>
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-gp-blue to-gp-magic transition-all duration-1000" style={{ width: `${(seconds / 60) * 100}%` }} /></div>
                <p className="mt-5 text-xs uppercase tracking-widest text-white/40">Card {question.number} of {question.total}</p>
                <h3 className="font-unbounded font-bold text-xl md:text-2xl mt-2 leading-snug">{question.prompt}</h3>
                <div className="grid sm:grid-cols-2 gap-3 mt-5" role="group" aria-label="Answer choices">
                  {question.choices.map((choice, index) => {
                    const state = feedback && (index === feedback.correct_index ? "border-gp-green bg-gp-green/15" : index === feedback.selected ? "border-gp-red bg-gp-red/15" : "border-white/10 opacity-55");
                    return <button key={choice} disabled={busy || !!feedback} onClick={() => answer(index)} className={`min-h-14 rounded-xl border p-3 text-left font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-gp-blue ${state || "border-white/10 bg-black/20 hover:border-gp-blue/60 hover:bg-gp-blue/10"}`}><span className="mr-2 text-white/35">{String.fromCharCode(65 + index)}.</span>{choice}</button>;
                  })}
                </div>
                {!feedback && <div className="mt-4"><Button onClick={revealHint} disabled={busy || !!hint} variant="ghost" className="text-gp-gold hover:bg-gp-gold/10"><Lightbulb size={16} className="mr-2" /> {hint || "Use a hint (-40 points)"}</Button>{hint && <p className="text-sm text-white/60 mt-1" aria-live="polite">{hint}</p>}</div>}
                {feedback && <div className={`mt-5 rounded-xl border p-4 ${feedback.correct ? "border-gp-green/30 bg-gp-green/10" : "border-gp-red/30 bg-gp-red/10"}`} aria-live="polite"><p className="font-bold flex items-center gap-2"><Brain size={17} /> {feedback.correct ? `Correct! +${feedback.points} points` : feedback.selected === -1 ? "Time's up" : "Not this time"}</p><p className="text-sm text-white/70 mt-1">{feedback.explanation}</p><div className="mt-4 flex flex-wrap items-center gap-3"><Button onClick={next} className="rounded-full bg-gp-blue hover:bg-gp-blue/90">{results?.pending ? "See results" : "Next card"}</Button>{feedback.learn_path && <a href={feedback.learn_path} className="text-sm font-semibold text-gp-gold hover:underline">Learn this game with Yorick →</a>}</div></div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
